import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import SegmentTimePicker from "../../../common/aqPickers/SegmentTimePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../common/esignature/esignatureConfirmService";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import {
  batchOptionLabel,
  findOption,
  getImpTxnRowKey,
  renderImpMovementCell,
  sanitizeEpsfDisplayLabel,
  toApiDateTimeString,
  useEpsfLedger,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import { formatReceiptStatusLabel, renderReceiptStatusBadge } from "../epsfBadges";
import {
  checkFormActionBlockers,
  confirmReturnRequest,
  confirmStockReturnRequest,
  createReturnRequest,
  createStockReturnRequest,
  fetchHeldReceipts,
  fetchImpKits,
  fetchReceiptDetail,
  fetchReturnWeeklyTrend,
  fetchReturnableDispenses,
  fetchStorageUnits,
  formBlockersNeedRequest,
  formBlockMessageForAction,
  postImpReturn,
  postImpStockReturn,
} from "../epsfService";
import {
  batchHeld,
  batchOnHand,
  suggestStockReturnReason,
} from "./inventory/inventoryDashboardUtils";
import ImpQtyKitFloatCell, {
  txnKitNumbers,
} from "./ImpQtyKitFloatCell";
import "../EpsfPages.scss";

/** Flip to true to show Create request on participant/stock return modals (backend ready). */
const SHOW_RETURN_CREATE_REQUEST = false;

const CONDITION_OPTIONS = [
  { value: "", label: "Select condition" },
  { value: "Acceptable", label: "Acceptable" },
  { value: "Damaged", label: "Damaged" },
  { value: "TemperatureConcern", label: "Temperature concern" },
  { value: "PackagingIssue", label: "Packaging issue" },
  { value: "QuantityDiscrepancy", label: "Quantity discrepancy" },
  { value: "Other", label: "Other" },
];

const STOCK_REASON_OPTIONS = [
  { value: "", label: "Select reason" },
  { value: "Damaged", label: "Damaged" },
  { value: "TemperatureExcursion", label: "Temperature excursion" },
  { value: "Expired", label: "Expired" },
  { value: "Recall", label: "Recall" },
  { value: "SponsorInstruction", label: "Sponsor instruction" },
  { value: "ExcessStock", label: "Excess stock" },
  { value: "RejectedAfterReview", label: "Rejected after review" },
  { value: "Other", label: "Other" },
];

const DESTINATION_OPTIONS = [
  { value: "", label: "Select destination" },
  { value: "Sponsor", label: "Sponsor" },
  { value: "Depot", label: "Depot" },
  { value: "Supplier", label: "Supplier" },
  { value: "Other", label: "Other" },
];

const UNIFIED_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "Quantity", label: "Qty" },
  { key: "ParticipantLabel", label: "Participant / Stock" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "TxnAt", label: "When" },
  { key: "CreatedByName", label: "Recorded by" },
  { key: "ConditionCode", label: "Condition" },
  { key: "ReferenceNo", label: "Reference" },
];

const UNIFIED_DEFAULT_COLUMNS = [
  "Product",
  "BatchNumber",
  "Quantity",
  "ParticipantLabel",
  "ReceiptStatus",
  "TxnAt",
  "CreatedByName",
];

function heldQtyOf(row) {
  const explicit = row?.HeldQuantity ?? row?.heldQuantity;
  if (explicit != null && explicit !== "") {
    const n = Number(explicit);
    return Number.isFinite(n) ? n : 0;
  }
  return Math.max(
    0,
    Number(row?.Quantity ?? row?.quantity ?? 0) -
      Number(row?.ReleasedQuantity ?? row?.releasedQuantity ?? 0) -
      Number(
        row?.ExternallyReturnedQuantity ?? row?.externallyReturnedQuantity ?? 0,
      ),
  );
}

function isReturnRequestedRow(row) {
  const status = String(row?.ReceiptStatus || row?.receiptStatus || "").trim();
  return status === "ReturnRequested" || status === "StockReturnRequested";
}

function isStockReturnRequestedRow(row) {
  return (
    String(row?.ReceiptStatus || row?.receiptStatus || "").trim() ===
    "StockReturnRequested"
  );
}

function returnPendingKitCount(row) {
  const n = Number(row?.ReturnPendingKitCount ?? row?.returnPendingKitCount ?? 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function isReturnPendingSource(row) {
  const status = String(row?.ReceiptStatus || row?.receiptStatus || "").trim();
  return status === "ReturnPending" || returnPendingKitCount(row) > 0;
}

function kitsForPendingReturn(heldRow, kits) {
  const txnId = String(heldRow?.ImpTxnId || heldRow?.impTxnId || "")
    .trim()
    .toLowerCase();
  if (!txnId) return [];
  return (kits || []).filter((k) => {
    if (String(k.Status || k.status || "") !== "ReturnPending") return false;
    return (
      String(k.ReceiptTxnId || k.receiptTxnId || "")
        .trim()
        .toLowerCase() === txnId
    );
  });
}

/** Pending queue units: kit count when tracked by kit, else held qty / 1. */
function pendingReturnUnits(heldRow, kits = []) {
  const kitRows = kitsForPendingReturn(heldRow, kits);
  if (kitRows.length > 0) return kitRows.length;
  const apiKits = returnPendingKitCount(heldRow);
  if (apiKits > 0) return apiKits;
  if (!isReturnPendingSource(heldRow)) return 0;
  const held = heldQtyOf(heldRow);
  return held > 0 ? held : 1;
}

/**
 * Build return-pending sources from held receipts + live kit statuses.
 * Mixed Quarantined receipts keep ReturnPending kits — do not rely on
 * receipt status alone.
 */
function buildPendingReturnSources(heldSources, siteKits) {
  const held = heldSources || [];
  const kits = siteKits || [];
  const pendingKitCounts = new Map();
  for (const k of kits) {
    if (String(k.Status || k.status || "") !== "ReturnPending") continue;
    const rid = String(k.ReceiptTxnId || k.receiptTxnId || "")
      .trim()
      .toLowerCase();
    if (!rid) continue;
    pendingKitCounts.set(rid, (pendingKitCounts.get(rid) || 0) + 1);
  }

  const byId = new Map();
  for (const h of held) {
    const id = String(h.ImpTxnId || h.impTxnId || "").trim();
    if (!id) continue;
    const key = id.toLowerCase();
    const fromKits = pendingKitCounts.get(key) || 0;
    const fromApi = returnPendingKitCount(h);
    const count = Math.max(fromKits, fromApi);
    const status = String(h.ReceiptStatus || h.receiptStatus || "").trim();
    if (status !== "ReturnPending" && count <= 0) continue;
    byId.set(key, {
      ...h,
      ReturnPendingKitCount: count > 0 ? count : fromApi,
    });
  }

  for (const [key, count] of pendingKitCounts) {
    if (byId.has(key)) continue;
    const pendingKits = kits.filter(
      (k) =>
        String(k.Status || k.status || "") === "ReturnPending" &&
        String(k.ReceiptTxnId || k.receiptTxnId || "")
          .trim()
          .toLowerCase() === key,
    );
    const first = pendingKits[0];
    if (!first) continue;
    byId.set(key, {
      ImpTxnId: first.ReceiptTxnId || first.receiptTxnId,
      ImpBatchId: first.ImpBatchId || first.impBatchId,
      ImpProductId: first.ImpProductId || first.impProductId,
      ProductCode: first.ProductCode || first.productCode,
      ProductName: first.ProductName || first.productName || "Study kit",
      BatchNumber: first.BatchNumber || first.batchNumber,
      InventoryTrackingMethod: "IndividualKit",
      ReceiptStatus: "ReturnPending",
      ReturnPendingKitCount: count,
      HeldQuantity: count,
      Quantity: count,
    });
  }

  return Array.from(byId.values());
}

function pendingReturnToRow(h, kits = []) {
  const id = String(h.ImpTxnId || h.impTxnId || "");
  const held = heldQtyOf(h);
  const pendingKitRows = kitsForPendingReturn(h, kits);
  const pendingKits =
    pendingKitRows.length > 0
      ? pendingKitRows.length
      : returnPendingKitCount(h);
  const isKit =
    h.InventoryTrackingMethod === "IndividualKit" ||
    h.inventoryTrackingMethod === "IndividualKit" ||
    pendingKits > 0;
  const qty = pendingKits > 0 ? pendingKits : held;
  const kitNumbers = pendingKitRows
    .map((k) => String(k.KitNumber || k.kitNumber || "").trim())
    .filter(Boolean)
    .join(", ");
  return {
    ...h,
    ImpTxnId: id,
    _pendingStockReturn: true,
    _returnKind: "stock-pending",
    InventoryTrackingMethod: isKit
      ? "IndividualKit"
      : h.InventoryTrackingMethod || h.inventoryTrackingMethod,
    TxnAt: null,
    ProductCode: h.ProductCode || h.productCode,
    ProductName: h.ProductName || h.productName,
    BatchNumber: h.BatchNumber || h.batchNumber,
    Quantity: qty,
    KitCount: isKit ? qty : null,
    KitNumbers: kitNumbers || null,
    ReceiptStatus: "ReturnPending",
    ConditionCode: null,
    ReferenceNo: null,
    CreatedByName: null,
    ParticipantLabel: null,
  };
}

function PendingReturnActionCell({
  row,
  onComplete,
  onCancelBlocked,
  busy = false,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const floatRef = useRef(null);

  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 220;
    const left = Math.min(
      Math.max(8, r.left),
      window.innerWidth - width - 8,
    );
    let top = r.bottom + 6;
    const approxH = 140;
    if (top + approxH > window.innerHeight - 8) {
      top = Math.max(8, r.top - approxH - 6);
    }
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePos();
    const onWin = () => updatePos();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      const t = ev.target;
      if (btnRef.current?.contains(t) || floatRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <div className="epsf-return-action-required">
        <span className="epsf-return-action-required__label">
          Action required
        </span>
        <button
          ref={btnRef}
          type="button"
          className="epsf-return-action-required__link"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={busy}
          onClick={() => setOpen((v) => !v)}
        >
          Complete action
        </button>
      </div>
      {open
        ? createPortal(
            <div
              ref={floatRef}
              className="epsf-return-action-float"
              role="dialog"
              aria-label="Return action"
              style={{ top: pos.top, left: pos.left }}
            >
              <p className="epsf-return-action-float__q">
                Do you want to complete the return or cancel this return?
              </p>
              <div className="epsf-return-action-float__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={busy}
                  onClick={() => {
                    setOpen(false);
                    onComplete?.(row);
                  }}
                >
                  Complete return
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={busy}
                  onClick={() => {
                    setOpen(false);
                    onCancelBlocked?.(row);
                  }}
                >
                  Cancel this return
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function txnAtMs(row) {
  const t = new Date(row?.TxnAt || row?.txnAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function pctChange(current, previous) {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0) {
    if (cur === 0) return 0;
    return 100;
  }
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}

function formatPctChange(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n)) return "0%";
  if (n > 0) return `+${n}%`;
  return `${n}%`;
}

function trendTone(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n === 0) return "is-flat";
  return n > 0 ? "is-up" : "is-down";
}

function trendIconClass(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n === 0) return "fa-minus";
  return n > 0 ? "fa-arrow-trend-up" : "fa-arrow-trend-down";
}

function returnSortRank(row) {
  if (row?._returnKind === "stock-pending") return 0;
  if (isReturnRequestedRow(row)) return 1;
  if (row?._returnKind === "participant") return 2;
  return 3;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowTimeHHmm() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function newClientRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ret-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const emptyParticipantForm = () => ({
  participantId: "",
  dispenseTxnId: "",
  quantity: "",
  txnDate: new Date(),
  txnTime: nowTimeHHmm(),
  conditionCode: "",
  storageUnitId: "",
  notes: "",
  selectedKitIds: [],
});

const emptyStockForm = () => ({
  destination: "Sponsor",
  impBatchId: "",
  sourceTxnId: "",
  quantity: "",
  txnDate: new Date(),
  txnTime: nowTimeHHmm(),
  reason: "RejectedAfterReview",
  referenceNo: "",
  courierRef: "",
  selectedKitIds: [],
});

function heldSourceOptionLabel(h, { includeBatch = true } = {}) {
  const product =
    sanitizeEpsfDisplayLabel(h.ProductName || h.productName) ||
    h.ProductCode ||
    h.productCode ||
    "Study kit";
  const batch = h.BatchNumber || h.batchNumber || "—";
  const held = heldQtyOf(h);
  const status =
    formatReceiptStatusLabel(h.ReceiptStatus || h.receiptStatus) ||
    h.ReceiptStatus ||
    h.receiptStatus ||
    "";
  const pending = returnPendingKitCount(h);
  const parts = [
    includeBatch ? `${product} / ${batch}` : null,
    held > 0 ? `held ${held}` : null,
    pending > 0 ? `${pending} return pending` : null,
    status || null,
  ].filter(Boolean);
  return parts.join(" · ") || "Held receipt";
}

function pendingReturnSourceSummary(h, batchRow = null) {
  if (!h && !batchRow) return "—";
  const product =
    sanitizeEpsfDisplayLabel(
      h?.ProductName ||
        h?.productName ||
        batchRow?.ProductName ||
        batchRow?.productName,
    ) ||
    h?.ProductCode ||
    batchRow?.ProductCode ||
    "Study kit";
  const batch =
    h?.BatchNumber ||
    h?.batchNumber ||
    batchRow?.BatchNumber ||
    batchRow?.batchNumber ||
    "—";
  const held = h ? heldQtyOf(h) : 0;
  const pending = h ? returnPendingKitCount(h) : 0;
  const status =
    formatReceiptStatusLabel(h?.ReceiptStatus || h?.receiptStatus) ||
    h?.ReceiptStatus ||
    "";
  const onHand =
    batchRow != null
      ? batchOnHand(batchRow)
      : null;
  const parts = [
    `${product} / ${batch}`,
    onHand != null ? `on hand ${onHand}` : null,
    held > 0 ? `held ${held}` : null,
    pending > 0 ? `${pending} return pending` : null,
    status || null,
  ].filter(Boolean);
  return parts.join(" · ");
}

export default function ImpReturns({
  overlay = false,
  preferImpProductId = null,
  preferImpBatchId = null,
  preferSourceTxnId = null,
  preferDispenseTxnId = null,
  onOverlayClose = null,
  onOverlaySuccess = null,
} = {}) {
  const { loading, canView, moduleAccess, canReturn } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state || {};

  const preferProductKey = String(
    preferImpProductId || navState.preferImpProductId || "",
  ).toLowerCase();
  const preferBatchId = String(
    preferImpBatchId || navState.preferImpBatchId || "",
  ).trim();
  const preferSourceId = String(
    preferSourceTxnId || navState.preferSourceTxnId || "",
  ).trim();
  const preferDispenseId = String(
    preferDispenseTxnId || navState.preferDispenseTxnId || "",
  ).trim();
  const openFromNav = Boolean(navState.openStockReturn);

  const participantLedger = useEpsfLedger(ctx.studyId, ctx.siteId, "Return");
  const stockLedger = useEpsfLedger(ctx.studyId, ctx.siteId, "StockReturn");

  /** Which return form is open: participant | stock (null when closed). */
  const [modalMode, setModalMode] = useState(null);
  const showModal = modalMode === "participant" || modalMode === "stock";
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [pForm, setPForm] = useState(emptyParticipantForm);
  const [sForm, setSForm] = useState(emptyStockForm);
  const [stockFromPending, setStockFromPending] = useState(false);
  /** Batch ids collapsed in Available stock / Returnable kits tree. */
  const [collapsedKitTreeBatches, setCollapsedKitTreeBatches] = useState(
    () => new Set(),
  );
  const [stockKitSearch, setStockKitSearch] = useState("");
  const [stockTrackOverview, setStockTrackOverview] = useState(null);
  const [stockTrackBusy, setStockTrackBusy] = useState(false);
  const [dispenses, setDispenses] = useState([]);
  const [kits, setKits] = useState([]);
  const [siteKits, setSiteKits] = useState([]);
  const [heldSources, setHeldSources] = useState([]);
  const [heldLoading, setHeldLoading] = useState(false);
  const [weeklyTrendRows, setWeeklyTrendRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [clientRequestId, setClientRequestId] = useState(newClientRequestId);
  const [formBlocked, setFormBlocked] = useState(false);
  const [formBlockMessage, setFormBlockMessage] = useState("");
  const [formGateBusy, setFormGateBusy] = useState(false);
  const [needsRequest, setNeedsRequest] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);

  const error = participantLedger.error || stockLedger.error;
  const setError = participantLedger.setError;
  const refresh = async () => {
    await participantLedger.refresh();
    await stockLedger.refresh();
  };

  const refreshHeldSources = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setHeldSources([]);
      setSiteKits([]);
      setWeeklyTrendRows([]);
      return;
    }
    setHeldLoading(true);
    try {
      const [hRes, kRes, tRes] = await Promise.all([
        fetchHeldReceipts(ctx.studyId, ctx.siteId),
        fetchImpKits(ctx.studyId, ctx.siteId),
        fetchReturnWeeklyTrend(ctx.studyId, ctx.siteId),
      ]);
      if (hRes.success) setHeldSources(hRes.rows || []);
      if (kRes.success) setSiteKits(kRes.rows || []);
      if (tRes.success) setWeeklyTrendRows(tRes.rows || []);
    } finally {
      setHeldLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  const participantOptions = useMemo(() => {
    const byId = new Map();
    (dispenses || []).forEach((d) => {
      const id = String(d.ParticipantId || d.participantId || "").trim();
      if (!id) return;
      if (byId.has(id)) return;
      byId.set(id, {
        value: id,
        label:
          sanitizeEpsfDisplayLabel(
            d.ParticipantLabel || d.participantLabel || "",
          ) || "Participant",
      });
    });
    const opts = Array.from(byId.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
    return [{ value: "", label: "Select participant" }, ...opts];
  }, [dispenses]);

  const participantDispenses = useMemo(() => {
    const pid = String(pForm.participantId || "")
      .trim()
      .toLowerCase();
    if (!pid) return [];
    return (dispenses || []).filter(
      (d) =>
        String(d.ParticipantId || d.participantId || "")
          .trim()
          .toLowerCase() === pid,
    );
  }, [dispenses, pForm.participantId]);

  const selectedDispense = useMemo(() => {
    const id = String(pForm.dispenseTxnId || "")
      .trim()
      .toLowerCase();
    if (!id) return null;
    return (
      participantDispenses.find(
        (d) =>
          String(d.DispenseTxnId || d.dispenseTxnId || "")
            .trim()
            .toLowerCase() === id,
      ) ||
      (dispenses || []).find(
        (d) =>
          String(d.DispenseTxnId || d.dispenseTxnId || "")
            .trim()
            .toLowerCase() === id,
      ) ||
      null
    );
  }, [dispenses, participantDispenses, pForm.dispenseTxnId]);

  const isParticipantKit =
    String(
      selectedDispense?.InventoryTrackingMethod ||
        selectedDispense?.inventoryTrackingMethod ||
        "",
    ) === "IndividualKit";

  const selectedParticipantLabel = useMemo(() => {
    const fromOpt = participantOptions.find(
      (o) => o.value && o.value === pForm.participantId,
    )?.label;
    if (fromOpt) return fromOpt;
    return (
      sanitizeEpsfDisplayLabel(
        selectedDispense?.ParticipantLabel ||
          selectedDispense?.participantLabel ||
          "",
      ) || ""
    );
  }, [participantOptions, pForm.participantId, selectedDispense]);

  /* Resolve participant from a pre-selected dispense (nav / overlay deep-link). */
  useEffect(() => {
    if (!pForm.dispenseTxnId || pForm.participantId) return;
    if (!dispenses?.length) return;
    const row = dispenses.find(
      (d) =>
        String(d.DispenseTxnId || d.dispenseTxnId || "")
          .trim()
          .toLowerCase() ===
        String(pForm.dispenseTxnId).trim().toLowerCase(),
    );
    const pid = String(row?.ParticipantId || row?.participantId || "").trim();
    if (!pid) return;
    setPForm((prev) =>
      prev.participantId === pid ? prev : { ...prev, participantId: pid },
    );
  }, [dispenses, pForm.dispenseTxnId, pForm.participantId]);

  const batchOptions = useMemo(
    () =>
      (stockLedger.balances || [])
        .filter((b) => Number(b.OnHand ?? b.onHand ?? 0) > 0)
        .filter((b) => {
          if (!preferProductKey) return true;
          return (
            String(b.ImpProductId || b.impProductId || "").toLowerCase() ===
            preferProductKey
          );
        })
        .map((b) => ({
          value: String(b.ImpBatchId || b.impBatchId),
          label: batchOptionLabel(b),
          row: b,
        })),
    [stockLedger.balances, preferProductKey],
  );

  const batchOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select batch" }, ...batchOptions],
    [batchOptions],
  );

  const selectedStockBatch = useMemo(
    () => batchOptions.find((o) => o.value === sForm.impBatchId)?.row || null,
    [batchOptions, sForm.impBatchId],
  );

  const isStockKit =
    String(
      selectedStockBatch?.InventoryTrackingMethod ||
        selectedStockBatch?.inventoryTrackingMethod ||
        "",
    ) === "IndividualKit";

  const unitOptions = useMemo(() => {
    const opts = (units || []).map((u) => ({
      value: String(u.StorageUnitId || u.storageUnitId),
      label: `${u.UnitCode || u.unitCode} — ${u.UnitName || u.unitName}`,
    }));
    return [{ value: "", label: "Select storage location" }, ...opts];
  }, [units]);

  const eligibleParticipantKits = useMemo(() => {
    const out = [];
    const seen = new Set();

    participantDispenses.forEach((d) => {
      const method = String(
        d.InventoryTrackingMethod || d.inventoryTrackingMethod || "",
      );
      if (method !== "IndividualKit") return;

      const dispenseTxnId = String(d.DispenseTxnId || d.dispenseTxnId || "");
      const impBatchId = String(d.ImpBatchId || d.impBatchId || "");
      const productCode = d.ProductCode || d.productCode || "";
      const batchNumber = d.BatchNumber || d.batchNumber || "";
      const remaining =
        d.RemainingReturnableQuantity ?? d.remainingReturnableQuantity ?? 0;
      const dispensed =
        d.DispensedQuantity ?? d.dispensedQuantity ?? d.Quantity ?? d.quantity;
      const alreadyReturned =
        d.AlreadyReturnedQuantity ?? d.alreadyReturnedQuantity ?? 0;

      const raw = d.KitsJson || d.kitsJson || d.KitsJSON || "";
      let parsed = null;
      if (raw) {
        try {
          parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        } catch {
          parsed = null;
        }
      }

      if (Array.isArray(parsed) && parsed.length) {
        parsed.forEach((k) => {
          const id = String(k.ImpKitId || k.impKitId || k.id || "");
          if (!id || seen.has(id)) return;
          seen.add(id);
          out.push({
            ImpKitId: id,
            KitNumber: k.KitNumber || k.kitNumber || k.kit || id,
            Status: k.Status || k.status || "Dispensed",
            DispenseTxnId: dispenseTxnId,
            ImpBatchId: impBatchId,
            ProductCode: productCode,
            BatchNumber: batchNumber,
            Remaining: remaining,
            Dispensed: dispensed,
            AlreadyReturned: alreadyReturned,
          });
        });
        return;
      }

      /* Fallback: site kits filtered to this participant + batch. */
      const participantId = String(
        d.ParticipantId || d.participantId || "",
      )
        .trim()
        .toLowerCase();
      const batchId = impBatchId.trim().toLowerCase();
      const dispenseId = dispenseTxnId.trim().toLowerCase();

      (kits || []).forEach((k) => {
        if (String(k.Status || k.status) !== "Dispensed") return;
        const id = String(k.ImpKitId || k.impKitId || "");
        if (!id || seen.has(id)) return;
        if (
          batchId &&
          String(k.ImpBatchId || k.impBatchId || "")
            .trim()
            .toLowerCase() !== batchId
        ) {
          return;
        }
        if (
          participantId &&
          String(k.CurrentParticipantId || k.currentParticipantId || "")
            .trim()
            .toLowerCase() !== participantId
        ) {
          return;
        }
        const linkedTxn = String(
          k.DispenseTxnId ||
            k.dispenseTxnId ||
            k.LastDispenseTxnId ||
            k.lastDispenseTxnId ||
            "",
        )
          .trim()
          .toLowerCase();
        if (dispenseId && linkedTxn && linkedTxn !== dispenseId) return;
        seen.add(id);
        out.push({
          ImpKitId: id,
          KitNumber: k.KitNumber || k.kitNumber || id,
          Status: "Dispensed",
          DispenseTxnId: dispenseTxnId,
          ImpBatchId: impBatchId,
          ProductCode: productCode,
          BatchNumber: batchNumber,
          Remaining: remaining,
          Dispensed: dispensed,
          AlreadyReturned: alreadyReturned,
        });
      });
    });

    return out;
  }, [participantDispenses, kits]);

  const eligibleBatchDispenses = useMemo(
    () =>
      participantDispenses.filter((d) => {
        const method = String(
          d.InventoryTrackingMethod || d.inventoryTrackingMethod || "",
        );
        return method !== "IndividualKit";
      }),
    [participantDispenses],
  );

  /* Group by batch so same-batch kits from multiple dispenses stay selectable together. */
  const participantKitGroups = useMemo(() => {
    const groups = new Map();
    eligibleParticipantKits.forEach((k) => {
      const key =
        String(k.ImpBatchId || "").toLowerCase() ||
        `${k.ProductCode || ""}|${k.BatchNumber || ""}`.toLowerCase() ||
        String(k.DispenseTxnId || "");
      if (!groups.has(key)) {
        groups.set(key, {
          groupKey: key,
          impBatchId: String(k.ImpBatchId || ""),
          productCode: k.ProductCode || "",
          batchNumber: k.BatchNumber || "",
          kits: [],
        });
      }
      groups.get(key).kits.push(k);
    });
    return Array.from(groups.values());
  }, [eligibleParticipantKits]);

  const selectedParticipantKits = useMemo(
    () =>
      eligibleParticipantKits.filter((k) =>
        pForm.selectedKitIds.includes(String(k.ImpKitId || k.impKitId || "")),
      ),
    [eligibleParticipantKits, pForm.selectedKitIds],
  );

  const selectedKitBatchSummary = useMemo(() => {
    if (!selectedParticipantKits.length) return null;
    const first = selectedParticipantKits[0];
    let dispensed = 0;
    let already = 0;
    let remaining = 0;
    const seenDisp = new Set();
    selectedParticipantKits.forEach((k) => {
      const dId = String(k.DispenseTxnId || "");
      if (!dId || seenDisp.has(dId)) return;
      seenDisp.add(dId);
      dispensed += Number(k.Dispensed ?? 0) || 0;
      already += Number(k.AlreadyReturned ?? 0) || 0;
      remaining += Number(k.Remaining ?? 0) || 0;
    });
    return {
      productCode: first.ProductCode || "",
      batchNumber: first.BatchNumber || "",
      impBatchId: first.ImpBatchId || "",
      dispensed,
      already,
      remaining,
      selectedCount: selectedParticipantKits.length,
    };
  }, [selectedParticipantKits]);

  /* Drop selected kits that are no longer returnable for this participant. */
  useEffect(() => {
    if (!pForm.participantId) return;
    const allowed = new Set(
      eligibleParticipantKits.map((k) => String(k.ImpKitId || k.impKitId)),
    );
    setPForm((prev) => {
      if (!prev.selectedKitIds.length) return prev;
      const next = prev.selectedKitIds.filter((id) => allowed.has(String(id)));
      if (next.length === prev.selectedKitIds.length) return prev;
      return {
        ...prev,
        selectedKitIds: next,
        quantity: next.length ? String(next.length) : "",
        dispenseTxnId: next.length ? prev.dispenseTxnId : "",
      };
    });
  }, [pForm.participantId, eligibleParticipantKits]);

  const eligibleStockKits = useMemo(() => {
    const sourceId = String(sForm.sourceTxnId || "")
      .trim()
      .toLowerCase();
    const source = heldSources.find(
      (h) =>
        String(h.ImpTxnId || h.impTxnId || "")
          .trim()
          .toLowerCase() === sourceId,
    );
    const batchId = String(sForm.impBatchId || "")
      .trim()
      .toLowerCase();

    const pool = (siteKits || []).length ? siteKits : kits;

    return (pool || []).filter((k) => {
      if (
        batchId &&
        String(k.ImpBatchId || k.impBatchId || "")
          .trim()
          .toLowerCase() !== batchId
      ) {
        return false;
      }
      const status = String(k.Status || k.status || "");
      const ok = [
        "Available",
        "Released",
        "Returned",
        "RequiresReview",
        "Hold",
        "Quarantined",
        "ReturnPending",
        "DestructionPending",
      ].includes(status);
      if (!ok) return false;

      if (sourceId) {
        const receiptId = String(k.ReceiptTxnId || k.receiptTxnId || "")
          .trim()
          .toLowerCase();
        if (receiptId && receiptId !== sourceId) return false;
      }

      /* Return-pending work: only those kits (not Available / still held). */
      if (isReturnPendingSource(source)) {
        return status === "ReturnPending";
      }
      /* Available stock: exclude kits already in return-pending. */
      if (status === "ReturnPending") return false;
      return true;
    });
  }, [kits, siteKits, sForm.sourceTxnId, sForm.impBatchId, heldSources]);

  /* Right panel: all returnable stock kits grouped by batch (or batch-qty lines). */
  const stockReturnKitGroups = useMemo(() => {
    const groups = [];
    const seenBatch = new Set();

    const batchList =
      stockFromPending && selectedStockBatch
        ? [selectedStockBatch]
        : (batchOptions || []).map((o) => o.row).filter(Boolean);

    batchList.forEach((b) => {
      const batchId = String(b.ImpBatchId || b.impBatchId || "");
      if (!batchId || seenBatch.has(batchId.toLowerCase())) return;
      seenBatch.add(batchId.toLowerCase());

      const method = String(
        b.InventoryTrackingMethod || b.inventoryTrackingMethod || "",
      );
      const productCode =
        b.ProductCode || b.productCode || b.ProductName || b.productName || "";
      const batchNumber = b.BatchNumber || b.batchNumber || "";
      const onHand = Number(b.OnHand ?? b.onHand ?? 0);
      const held = Number(b.HeldQuantity ?? b.heldQuantity ?? 0);

      if (method === "IndividualKit") {
        const sourceId = String(sForm.sourceTxnId || "")
          .trim()
          .toLowerCase();
        const source = heldSources.find(
          (h) =>
            String(h.ImpTxnId || h.impTxnId || "")
              .trim()
              .toLowerCase() === sourceId,
        );
        const pendingOnly =
          stockFromPending || isReturnPendingSource(source);

        const batchKits = (siteKits || []).filter((k) => {
          if (
            String(k.ImpBatchId || k.impBatchId || "")
              .trim()
              .toLowerCase() !== batchId.toLowerCase()
          ) {
            return false;
          }
          const status = String(k.Status || k.status || "");
          const ok = [
            "Available",
            "Released",
            "Returned",
            "RequiresReview",
            "Hold",
            "Quarantined",
            "ReturnPending",
            "DestructionPending",
          ].includes(status);
          if (!ok) return false;
          if (pendingOnly && status !== "ReturnPending") return false;
          /* Available stock panel: ReturnPending kits belong in pending, not here. */
          if (!pendingOnly && status === "ReturnPending") return false;
          if (sourceId && !stockFromPending) {
            const receiptId = String(k.ReceiptTxnId || k.receiptTxnId || "")
              .trim()
              .toLowerCase();
            if (receiptId && receiptId !== sourceId) return false;
          }
          return true;
        });

        if (!batchKits.length) return;
        groups.push({
          type: "kits",
          batchId,
          productCode,
          batchNumber,
          onHand,
          held,
          kits: batchKits.map((k) => ({
            ImpKitId: String(k.ImpKitId || k.impKitId || ""),
            KitNumber: k.KitNumber || k.kitNumber || "",
            Status: k.Status || k.status || "",
            ImpBatchId: batchId,
          })),
        });
        return;
      }

      if (onHand <= 0 && held <= 0) return;
      groups.push({
        type: "batch",
        batchId,
        productCode,
        batchNumber,
        onHand,
        held,
        kits: [],
      });
    });

    return groups;
  }, [
    batchOptions,
    siteKits,
    selectedStockBatch,
    stockFromPending,
    sForm.sourceTxnId,
    heldSources,
  ]);

  const filteredStockReturnKitGroups = useMemo(() => {
    const q = String(stockKitSearch || "")
      .trim()
      .toLowerCase();
    if (!q) return stockReturnKitGroups;
    return stockReturnKitGroups
      .map((group) => {
        const batchHit = [group.productCode, group.batchNumber, group.batchId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
        if (group.type !== "kits") {
          return batchHit ? group : null;
        }
        if (batchHit) return group;
        const kits = (group.kits || []).filter((k) => {
          const no = String(k.KitNumber || k.kitNumber || k.ImpKitId || "");
          const st = String(k.Status || k.status || "");
          return (
            no.toLowerCase().includes(q) || st.toLowerCase().includes(q)
          );
        });
        if (!kits.length) return null;
        return { ...group, kits };
      })
      .filter(Boolean);
  }, [stockReturnKitGroups, stockKitSearch]);

  const selectedStockKitBatchSummary = useMemo(() => {
    if (!selectedStockBatch && !sForm.selectedKitIds.length) return null;
    return {
      productCode:
        selectedStockBatch?.ProductCode ||
        selectedStockBatch?.productCode ||
        selectedStockBatch?.ProductName ||
        selectedStockBatch?.productName ||
        "",
      batchNumber:
        selectedStockBatch?.BatchNumber ||
        selectedStockBatch?.batchNumber ||
        "",
      onHand: selectedStockBatch
        ? Number(
            selectedStockBatch.OnHand ?? selectedStockBatch.onHand ?? 0,
          )
        : 0,
      held: selectedStockBatch
        ? Number(
            selectedStockBatch.HeldQuantity ??
              selectedStockBatch.heldQuantity ??
              0,
          )
        : 0,
      selectedCount: sForm.selectedKitIds.length,
    };
  }, [selectedStockBatch, sForm.selectedKitIds.length]);

  const stockSourceOptions = useMemo(() => {
    const none = {
      value: "",
      label: "None — return from available / on-hand",
    };
    const batchId = String(sForm.impBatchId || "");
    const currentId = String(sForm.sourceTxnId || "").trim().toLowerCase();
    const includeBatch = !batchId;
    const rows = (heldSources || []).filter((h) => {
      const id = String(h.ImpTxnId || h.impTxnId || "")
        .trim()
        .toLowerCase();
      if (currentId && id === currentId) return true;
      if (!batchId) return true;
      return String(h.ImpBatchId || h.impBatchId || "") === batchId;
    });
    const opts = rows.map((h) => ({
      value: String(h.ImpTxnId || h.impTxnId || ""),
      label: heldSourceOptionLabel(h, { includeBatch }),
    }));
    if (
      currentId &&
      !opts.some((o) => String(o.value).trim().toLowerCase() === currentId)
    ) {
      const orphan = (heldSources || []).find(
        (h) =>
          String(h.ImpTxnId || h.impTxnId || "")
            .trim()
            .toLowerCase() === currentId,
      );
      if (orphan) {
        opts.unshift({
          value: String(orphan.ImpTxnId || orphan.impTxnId || ""),
          label: heldSourceOptionLabel(orphan, { includeBatch }),
        });
      }
    }
    return [none, ...opts];
  }, [heldSources, sForm.impBatchId, sForm.sourceTxnId]);

  const selectedHeldSource = useMemo(() => {
    const sourceId = String(sForm.sourceTxnId || "")
      .trim()
      .toLowerCase();
    if (!sourceId) return null;
    return (
      (heldSources || []).find(
        (h) =>
          String(h.ImpTxnId || h.impTxnId || "")
            .trim()
            .toLowerCase() === sourceId,
      ) || null
    );
  }, [heldSources, sForm.sourceTxnId]);

  const pendingSourceSummary = useMemo(
    () =>
      pendingReturnSourceSummary(selectedHeldSource, selectedStockBatch),
    [selectedHeldSource, selectedStockBatch],
  );

  const otherBatchReturnPendingKits = useMemo(() => {
    if (!sForm.impBatchId || !isStockKit) return [];
    const batchId = String(sForm.impBatchId);
    const sourceId = String(sForm.sourceTxnId || "")
      .trim()
      .toLowerCase();
    const selected = new Set(sForm.selectedKitIds.map(String));
    return (siteKits || []).filter((k) => {
      if (String(k.ImpBatchId || k.impBatchId || "") !== batchId) return false;
      if (String(k.Status || k.status || "") !== "ReturnPending") return false;
      const id = String(k.ImpKitId || k.impKitId || "");
      if (!id || selected.has(id)) return false;
      /* Prefer same-source kits first for Add; include all same-batch. */
      return true;
    }).map((k) => {
      const receiptId = String(k.ReceiptTxnId || k.receiptTxnId || "")
        .trim()
        .toLowerCase();
      return {
        ...k,
        _sameSource: Boolean(sourceId) && receiptId === sourceId,
      };
    });
  }, [
    siteKits,
    sForm.impBatchId,
    sForm.sourceTxnId,
    sForm.selectedKitIds,
    isStockKit,
  ]);

  const relatedBatchPendingElsewhere = useMemo(
    () => (otherBatchReturnPendingKits || []).filter((k) => !k._sameSource),
    [otherBatchReturnPendingKits],
  );

  const sameSourceOtherPendingKits = useMemo(
    () => (otherBatchReturnPendingKits || []).filter((k) => k._sameSource),
    [otherBatchReturnPendingKits],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (
        !showModal ||
        modalMode !== "stock" ||
        !ctx.studyId ||
        !ctx.siteId ||
        !sForm.sourceTxnId
      ) {
        setStockTrackOverview(null);
        return;
      }
      setStockTrackBusy(true);
      try {
        const detail = await fetchReceiptDetail(
          ctx.studyId,
          ctx.siteId,
          sForm.sourceTxnId,
        );
        if (cancelled) return;
        const ds = detail.ds || {};
        const tables = Object.keys(ds)
          .filter((k) => Array.isArray(ds[k]))
          .map((k) => ds[k]);
        const overview = tables[0]?.[0] || detail.rows?.[0] || null;
        setStockTrackOverview(overview);
      } finally {
        if (!cancelled) setStockTrackBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, modalMode, ctx.studyId, ctx.siteId, sForm.sourceTxnId]);

  const stockInitiatedBy = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    return (
      o.ReleasedByName ||
      o.releasedByName ||
      o.ReceivedByName ||
      o.receivedByName ||
      ""
    );
  }, [stockTrackOverview]);

  const stockInitiatedAt = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    const raw = o.ReleasedAt || o.releasedAt || o.TxnAt || o.txnAt || null;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString();
  }, [stockTrackOverview]);

  const stockReviewComment = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    return String(o.ReleaseComment || o.releaseComment || "").trim();
  }, [stockTrackOverview]);

  const addOtherPendingKit = (kitId) => {
    setSForm((prev) => {
      if (prev.selectedKitIds.includes(kitId)) return prev;
      const ids = [...prev.selectedKitIds, kitId];
      return { ...prev, selectedKitIds: ids, quantity: String(ids.length) };
    });
  };

  const pendingReturnSources = useMemo(
    () => buildPendingReturnSources(heldSources, siteKits),
    [heldSources, siteKits],
  );

  useEffect(() => {
    refreshHeldSources();
  }, [refreshHeldSources]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showModal || !ctx.studyId || !ctx.siteId) return;
      if (modalMode === "participant") {
        const [dRes, uRes] = await Promise.all([
          fetchReturnableDispenses(ctx.studyId, ctx.siteId),
          fetchStorageUnits(ctx.studyId, ctx.siteId),
        ]);
        if (cancelled) return;
        if (dRes.success) setDispenses(dRes.rows || []);
        if (uRes.success) setUnits(uRes.rows || []);
      } else if (modalMode === "stock") {
        const uRes = await fetchStorageUnits(ctx.studyId, ctx.siteId);
        if (!cancelled && uRes.success) setUnits(uRes.rows || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, ctx.studyId, ctx.siteId, modalMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const batchId =
        modalMode === "participant"
          ? selectedDispense?.ImpBatchId || selectedDispense?.impBatchId
          : sForm.impBatchId;
      const needKits =
        showModal &&
        ctx.studyId &&
        ctx.siteId &&
        batchId &&
        (modalMode === "participant" ? isParticipantKit : isStockKit);
      if (!needKits) {
        setKits([]);
        return;
      }
      const result = await fetchImpKits(ctx.studyId, ctx.siteId, {
        impBatchId: batchId,
      });
      if (!cancelled && result.success) setKits(result.rows || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    ctx.studyId,
    ctx.siteId,
    modalMode,
    selectedDispense,
    sForm.impBatchId,
    isParticipantKit,
    isStockKit,
  ]);

  /* When return-pending source is chosen, pre-select those kits. */
  useEffect(() => {
    if (!showModal || modalMode !== "stock" || !isStockKit) return;
    if (!sForm.sourceTxnId) return;
    const source = heldSources.find(
      (h) =>
        String(h.ImpTxnId || h.impTxnId || "") === String(sForm.sourceTxnId),
    );
    if (!isReturnPendingSource(source)) {
      return;
    }
    const pendingIds = eligibleStockKits
      .filter((k) => String(k.Status || k.status) === "ReturnPending")
      .map((k) => String(k.ImpKitId || k.impKitId))
      .filter(Boolean);
    if (!pendingIds.length) return;
    setSForm((prev) => {
      const same =
        prev.selectedKitIds.length === pendingIds.length &&
        pendingIds.every((id) => prev.selectedKitIds.includes(id));
      if (same) return prev;
      return {
        ...prev,
        selectedKitIds: pendingIds,
        quantity: String(pendingIds.length),
      };
    });
  }, [
    showModal,
    modalMode,
    isStockKit,
    sForm.sourceTxnId,
    heldSources,
    eligibleStockKits,
  ]);

  const openAdd = (mode = "participant") => {
    setModalMode(mode);
    setPForm(emptyParticipantForm());
    setSForm(emptyStockForm());
    setStockFromPending(false);
    setCollapsedKitTreeBatches(new Set());
    setStockKitSearch("");
    setKits([]);
    setClientRequestId(newClientRequestId());
    setFormError("");
    setError("");
  };

  const openStockReturnFromPending = useCallback((heldRow) => {
    const batchId = String(heldRow?.ImpBatchId || heldRow?.impBatchId || "");
    const txnId = String(heldRow?.ImpTxnId || heldRow?.impTxnId || "");
    setModalMode("stock");
    setPForm(emptyParticipantForm());
    setStockFromPending(true);
    setCollapsedKitTreeBatches(new Set());
    setStockKitSearch("");
    setSForm({
      ...emptyStockForm(),
      destination: "Sponsor",
      impBatchId: batchId,
      sourceTxnId: txnId,
      reason: "RejectedAfterReview",
    });
    setKits([]);
    setClientRequestId(newClientRequestId());
    setFormError("");
    setError("");
  }, [setError]);

  const notifyCancelReturnPendingBlocked = useCallback(() => {
    showAlert({
      title: "Cancel return",
      message:
        "This is in progress. Wait for some time to enable it.",
      type: "info",
      buttonText: "OK",
    });
  }, [showAlert]);

  const onConfirmReturnRequest = useCallback(
    async (row) => {
      const txnId = String(row?.ImpTxnId || row?.impTxnId || "").trim();
      if (!txnId || !ctx.studyId || !ctx.siteId) return;
      setBusy(true);
      setError("");
      try {
        const isStock = isStockReturnRequestedRow(row);
        const result = isStock
          ? await confirmStockReturnRequest({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              ImpTxnId: txnId,
            })
          : await confirmReturnRequest({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              ImpTxnId: txnId,
            });
        if (!result.success) {
          showAlert({
            title: "Confirm return failed",
            message: result.message || "Confirm return failed.",
            type: "danger",
            buttonText: "OK",
          });
          return;
        }
        await refresh();
        await refreshHeldSources();
      } finally {
        setBusy(false);
      }
    },
    [ctx.studyId, ctx.siteId, refresh, refreshHeldSources, setError, showAlert],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === "ParticipantLabel") {
        if (row._returnKind === "participant") {
          return renderImpMovementCell(row, field);
        }
        return (
          <span className="epsf-return-type epsf-return-type--stock">
            Stock
          </span>
        );
      }
      if (field === "Quantity") {
        return <ImpQtyKitFloatCell row={row} />;
      }
      if (field === "ReceiptStatus") {
        const status = row.ReceiptStatus || row.receiptStatus;
        if (isReturnRequestedRow(row)) {
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              {renderReceiptStatusBadge(
                isStockReturnRequestedRow(row)
                  ? "StockReturnRequested"
                  : "ReturnRequested",
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                title="Confirm return (final post)"
                disabled={busy}
                onClick={() => onConfirmReturnRequest(row)}
              >
                Confirm return
              </button>
            </div>
          );
        }
        return status ? renderReceiptStatusBadge(status) : "—";
      }
      if (field === "TxnAt") {
        if (row._pendingStockReturn) {
          return (
            <PendingReturnActionCell
              row={row}
              onComplete={openStockReturnFromPending}
              onCancelBlocked={notifyCancelReturnPendingBlocked}
            />
          );
        }
        return renderImpMovementCell(row, field);
      }
      if (row._pendingStockReturn && field === "ConditionCode") return "—";
      if (row._pendingStockReturn && field === "ReferenceNo") return "—";
      if (row._pendingStockReturn && field === "CreatedByName") return "—";
      return renderImpMovementCell(row, field);
    },
    [
      openStockReturnFromPending,
      notifyCancelReturnPendingBlocked,
      onConfirmReturnRequest,
      busy,
    ],
  );

  const unifiedTableRows = useMemo(() => {
    const pending = (pendingReturnSources || []).map((h) =>
      pendingReturnToRow(h, siteKits),
    );
    const participant = (participantLedger.txns || []).map((r) => ({
      ...r,
      _returnKind: "participant",
      KitNumbers: txnKitNumbers(r),
    }));
    const stock = (stockLedger.txns || []).map((r) => ({
      ...r,
      _returnKind: "stock",
      ReceiptStatus: r.ReceiptStatus || r.receiptStatus || "Returned",
      KitNumbers: txnKitNumbers(r),
    }));
    return [...pending, ...participant, ...stock].sort((a, b) => {
      const rank = returnSortRank(a) - returnSortRank(b);
      if (rank !== 0) return rank;
      return txnAtMs(b) - txnAtMs(a);
    });
  }, [
    pendingReturnSources,
    siteKits,
    participantLedger.txns,
    stockLedger.txns,
  ]);

  const returnSummary = useMemo(() => {
    const pending = (pendingReturnSources || []).reduce(
      (sum, h) => sum + pendingReturnUnits(h, siteKits),
      0,
    );
    const participant = (participantLedger.txns || []).length;
    const stock = (stockLedger.txns || []).length;
    return { pending, participant, stock, total: pending + participant + stock };
  }, [
    pendingReturnSources,
    siteKits,
    participantLedger.txns,
    stockLedger.txns,
  ]);

  const weeklyTrend = useMemo(() => {
    const rows = (weeklyTrendRows || []).map((r) => ({
      weekLabel: String(r.WeekLabel || r.weekLabel || ""),
      completed: Number(r.CompletedCount ?? r.completedCount ?? 0) || 0,
      cancelled: Number(r.CancelledCount ?? r.cancelledCount ?? 0) || 0,
      isCurrent: Number(r.IsCurrentWeek ?? r.isCurrentWeek ?? 0) === 1,
    }));
    const thisWeek = rows.find((r) => r.isCurrent) || rows[rows.length - 1] || null;
    const lastWeek =
      rows.length >= 2
        ? rows[rows.length - 2]
        : { completed: 0, cancelled: 0 };
    const completedPct = pctChange(
      thisWeek?.completed ?? 0,
      lastWeek?.completed ?? 0,
    );
    const cancelledPct = pctChange(
      thisWeek?.cancelled ?? 0,
      lastWeek?.cancelled ?? 0,
    );
    return {
      rows,
      thisWeekCompleted: thisWeek?.completed ?? 0,
      thisWeekCancelled: thisWeek?.cancelled ?? 0,
      completedPct,
      cancelledPct,
    };
  }, [weeklyTrendRows]);

  const getUnifiedRowKey = useCallback((row) => {
    if (row?._pendingStockReturn) {
      return `pending-return-${row.ImpTxnId || row.impTxnId}`;
    }
    const kind = row?._returnKind || "txn";
    return `${kind}-${getImpTxnRowKey(row)}`;
  }, []);

  const closeModal = () => {
    if (busy || requestBusy) return;
    setModalMode(null);
    setStockFromPending(false);
    setCollapsedKitTreeBatches(new Set());
    setStockKitSearch("");
    setFormError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    if (overlay) onOverlayClose?.();
  };

  const refreshReturnFormGate = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return;
    }
    setFormGateBusy(true);
    try {
      const res = await checkFormActionBlockers(
        ctx.studyId,
        ctx.siteId,
        "CONFIRM_PARTICIPANT_RETURN",
      );
      const message = formBlockMessageForAction(res, "Post return");
      const needReq = formBlockersNeedRequest(res);
      if (res.success && message) {
        /* Without Create request UI, NeedsRequest must not block Post. */
        if (needReq && !SHOW_RETURN_CREATE_REQUEST) {
          setFormBlocked(false);
          setFormBlockMessage("");
          setNeedsRequest(false);
        } else {
          setFormBlocked(true);
          setFormBlockMessage(message);
          setNeedsRequest(needReq);
        }
      } else if (!res.success) {
        setFormBlocked(true);
        setFormBlockMessage(
          res.message ||
            "Could not verify return form requirements. Post is blocked until the check succeeds.",
        );
        setNeedsRequest(false);
      } else {
        setFormBlocked(false);
        setFormBlockMessage("");
        setNeedsRequest(false);
      }
    } catch {
      setFormBlocked(true);
      setFormBlockMessage(
        "Could not verify return form requirements. Post is blocked until the check succeeds.",
      );
      setNeedsRequest(false);
    } finally {
      setFormGateBusy(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    if (modalMode !== "participant" && modalMode !== "stock") {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await refreshReturnFormGate();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [modalMode, refreshReturnFormGate]);

  useEffect(() => {
    if (!overlay) return undefined;
    const dispenseId = preferDispenseId;
    if (dispenseId) {
      setModalMode("participant");
      setPForm({
        ...emptyParticipantForm(),
        dispenseTxnId: dispenseId,
      });
      setSForm(emptyStockForm());
      setKits([]);
      setClientRequestId(newClientRequestId());
      setFormError("");
      setError("");
    } else {
      setModalMode("stock");
      setPForm(emptyParticipantForm());
      setStockFromPending(Boolean(preferSourceId));
      setSForm({
        ...emptyStockForm(),
        impBatchId: preferBatchId || "",
        sourceTxnId: preferSourceId || "",
      });
      setKits([]);
      setClientRequestId(newClientRequestId());
      setFormError("");
      setError("");
    }
    return undefined;
    // Mount-once for overlay launch from Inventory / Dispense.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  /* Land from Review held stock (Receipt) with Return pending. */
  useEffect(() => {
    if (overlay || !openFromNav) return undefined;
    setModalMode("stock");
    setPForm(emptyParticipantForm());
    setStockFromPending(Boolean(preferSourceId || preferBatchId));
    setSForm({
      ...emptyStockForm(),
      destination: "Sponsor",
      impBatchId: preferBatchId || "",
      sourceTxnId: preferSourceId || "",
      reason: "RejectedAfterReview",
    });
    setKits([]);
    setClientRequestId(newClientRequestId());
    setFormError("");
    setError("");
    navigate(location.pathname, { replace: true, state: null });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFromNav]);

  /* Land from Dispense list — open participant return for a dispense. */
  useEffect(() => {
    if (overlay) return undefined;
    const openParticipant = Boolean(navState.openParticipantReturn);
    const dispenseId = String(
      navState.preferDispenseTxnId || preferDispenseId || "",
    ).trim();
    if (!openParticipant || !dispenseId) return undefined;

    setModalMode("participant");
    setPForm({
      ...emptyParticipantForm(),
      dispenseTxnId: dispenseId,
    });
    setSForm(emptyStockForm());
    setKits([]);
    setClientRequestId(newClientRequestId());
    setFormError("");
    setError("");
    navigate(location.pathname, { replace: true, state: null });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navState.openParticipantReturn, navState.preferDispenseTxnId]);

  // Auto-select batch / reason / qty / held source once options load (overlay).
  useEffect(() => {
    if (!overlay || !showModal || modalMode !== "stock") return;
    if (!batchOptions.length) return;

    setSForm((prev) => {
      let next = { ...prev };
      let changed = false;

      if (!next.impBatchId) {
        const preferred =
          preferBatchId &&
          batchOptions.some((o) => o.value === preferBatchId)
            ? preferBatchId
            : batchOptions[0].value;
        if (preferred) {
          next.impBatchId = preferred;
          changed = true;
        }
      }

      const row =
        batchOptions.find((o) => o.value === next.impBatchId)?.row || null;
      const tracking = String(
        row?.InventoryTrackingMethod || row?.inventoryTrackingMethod || "",
      );
      const isKit = tracking === "IndividualKit";

      if (!next.reason) {
        const suggested = suggestStockReturnReason(row);
        if (suggested) {
          next.reason = suggested;
          changed = true;
        }
      }

      if (!isKit && !next.quantity && row) {
        const heldQty = batchHeld(row);
        const qty = heldQty > 0 ? heldQty : batchOnHand(row);
        if (qty > 0) {
          next.quantity = String(qty);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [overlay, showModal, modalMode, batchOptions, preferBatchId]);

  useEffect(() => {
    if (!overlay || !showModal || modalMode !== "stock") return;
    if (!sForm.impBatchId || !heldSources.length) return;
    if (sForm.sourceTxnId) return;

    const heldForBatch = heldSources.filter(
      (h) =>
        String(h.ImpBatchId || h.impBatchId || "") === String(sForm.impBatchId),
    );

    let pick = null;
    if (preferSourceId) {
      pick =
        heldForBatch.find(
          (h) =>
            String(h.ImpTxnId || h.impTxnId || "") === preferSourceId,
        ) ||
        heldSources.find(
          (h) =>
            String(h.ImpTxnId || h.impTxnId || "") === preferSourceId,
        ) ||
        null;
    }
    if (!pick && heldForBatch.length === 1) pick = heldForBatch[0];
    if (!pick) return;

    const txnId = String(pick.ImpTxnId || pick.impTxnId || "");
    if (!txnId) return;
    setSForm((prev) =>
      prev.sourceTxnId ? prev : { ...prev, sourceTxnId: txnId },
    );
  }, [
    overlay,
    showModal,
    modalMode,
    sForm.impBatchId,
    sForm.sourceTxnId,
    heldSources,
    preferSourceId,
  ]);

  const toggleKitTreeBatch = (batchKey) => {
    const key = String(batchKey || "");
    if (!key) return;
    setCollapsedKitTreeBatches((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleKit = (kitId, which) => {
    if (which === "p") {
      const kitMeta = eligibleParticipantKits.find(
        (k) => String(k.ImpKitId || k.impKitId) === String(kitId),
      );
      const kitBatchKey = String(
        kitMeta?.ImpBatchId ||
          `${kitMeta?.ProductCode || ""}|${kitMeta?.BatchNumber || ""}` ||
          "",
      ).toLowerCase();
      const kitDispenseId = String(kitMeta?.DispenseTxnId || "");
      setPForm((prev) => {
        const currentSelected = eligibleParticipantKits.filter((k) =>
          prev.selectedKitIds.includes(String(k.ImpKitId || k.impKitId || "")),
        );
        const currentBatchKey = currentSelected.length
          ? String(
              currentSelected[0].ImpBatchId ||
                `${currentSelected[0].ProductCode || ""}|${currentSelected[0].BatchNumber || ""}` ||
                "",
            ).toLowerCase()
          : "";
        const switchingBatch =
          Boolean(kitBatchKey) &&
          Boolean(currentBatchKey) &&
          kitBatchKey !== currentBatchKey;
        const set = new Set(switchingBatch ? [] : prev.selectedKitIds);
        if (set.has(kitId)) set.delete(kitId);
        else set.add(kitId);
        const ids = Array.from(set);
        const firstMeta = eligibleParticipantKits.find(
          (k) => String(k.ImpKitId || k.impKitId) === String(ids[0] || ""),
        );
        return {
          ...prev,
          dispenseTxnId: ids.length
            ? String(firstMeta?.DispenseTxnId || kitDispenseId || "")
            : "",
          selectedKitIds: ids,
          quantity: ids.length ? String(ids.length) : "",
        };
      });
      return;
    }
    setSForm((prev) => {
      const kitMeta = stockReturnKitGroups
        .flatMap((g) => g.kits || [])
        .find((k) => String(k.ImpKitId || k.impKitId) === String(kitId));
      const kitBatchId = String(kitMeta?.ImpBatchId || "");
      const switchingBatch =
        kitBatchId &&
        prev.impBatchId &&
        String(prev.impBatchId) !== kitBatchId;
      const set = new Set(switchingBatch ? [] : prev.selectedKitIds);
      if (set.has(kitId)) set.delete(kitId);
      else set.add(kitId);
      const ids = Array.from(set);
      return {
        ...prev,
        impBatchId: ids.length
          ? kitBatchId || prev.impBatchId
          : stockFromPending
            ? prev.impBatchId
            : "",
        sourceTxnId:
          switchingBatch && !stockFromPending ? "" : prev.sourceTxnId,
        selectedKitIds: ids,
        quantity: ids.length ? String(ids.length) : "",
      };
    });
  };

  const selectStockBatchLine = (batchId) => {
    const id = String(batchId || "");
    const row = batchOptions.find((o) => o.value === id)?.row || null;
    const heldQty = row ? batchHeld(row) : 0;
    const onHandQty = row ? batchOnHand(row) : 0;
    const qty = heldQty > 0 ? heldQty : onHandQty;
    setSForm((prev) => ({
      ...prev,
      impBatchId: id,
      sourceTxnId: stockFromPending ? prev.sourceTxnId : "",
      selectedKitIds: [],
      quantity: qty > 0 ? String(qty) : "",
    }));
  };

  /** Select / clear every kit under a stock batch (same-batch rule). */
  const toggleStockBatchAllKits = (group) => {
    if (!group || group.type !== "kits") return;
    const batchId = String(group.batchId || "");
    const kitIds = (group.kits || [])
      .map((k) => String(k.ImpKitId || k.impKitId || ""))
      .filter(Boolean);
    if (!batchId || !kitIds.length) return;

    setCollapsedKitTreeBatches((prev) => {
      const next = new Set(prev);
      next.delete(batchId);
      return next;
    });

    setSForm((prev) => {
      const allSelected =
        kitIds.length > 0 &&
        kitIds.every((id) => prev.selectedKitIds.includes(id));
      if (allSelected) {
        return {
          ...prev,
          selectedKitIds: [],
          quantity: "",
          impBatchId: stockFromPending ? prev.impBatchId : "",
          sourceTxnId: stockFromPending ? prev.sourceTxnId : "",
        };
      }
      return {
        ...prev,
        impBatchId: batchId,
        sourceTxnId:
          String(prev.impBatchId) === batchId
            ? prev.sourceTxnId
            : stockFromPending
              ? prev.sourceTxnId
              : "",
        selectedKitIds: kitIds,
        quantity: String(kitIds.length),
      };
    });
  };

  /** Select / clear every kit under a participant batch group. */
  const toggleParticipantBatchAllKits = (group) => {
    if (!group?.kits?.length) return;
    const kitIds = group.kits
      .map((k) => String(k.ImpKitId || k.impKitId || ""))
      .filter(Boolean);
    if (!kitIds.length) return;

    setCollapsedKitTreeBatches((prev) => {
      const key = String(
        group.groupKey ||
          `${group.productCode}|${group.batchNumber}` ||
          "kits",
      );
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    setPForm((prev) => {
      const allSelected =
        kitIds.length > 0 &&
        kitIds.every((id) => prev.selectedKitIds.includes(id));
      if (allSelected) {
        return {
          ...prev,
          selectedKitIds: [],
          quantity: "",
          dispenseTxnId: "",
        };
      }
      const first = group.kits[0];
      return {
        ...prev,
        selectedKitIds: kitIds,
        quantity: String(kitIds.length),
        dispenseTxnId: String(first?.DispenseTxnId || ""),
      };
    });
  };

  const selectBatchDispenseLine = (dispenseTxnId) => {
    const id = String(dispenseTxnId || "");
    setPForm((prev) => ({
      ...prev,
      dispenseTxnId: id,
      selectedKitIds: [],
      quantity: "",
    }));
  };

  const onSubmitParticipant = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!canReturn && !canView) {
      setFormError("You do not have permission to record returns.");
      return;
    }
    if (formBlocked || formGateBusy) {
      setFormError(
        formBlockMessage ||
          "Post participant return is blocked until the required form is completed and approved.",
      );
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !pForm.dispenseTxnId) {
      setFormError(
        pForm.participantId
          ? "Select returnable kits (or a batch line) on the right."
          : "Select a participant.",
      );
      return;
    }
    if (!pForm.conditionCode) {
      setFormError("Return condition is required.");
      return;
    }
    if (!pForm.storageUnitId) {
      setFormError("Storage location is required.");
      return;
    }
    if (isParticipantKit) {
      if (!pForm.selectedKitIds.length) {
        setFormError("Select dispensed kits to return.");
        return;
      }
    } else if (!(Number(pForm.quantity) > 0)) {
      setFormError("Enter a positive return quantity.");
      return;
    }

    const qty = isParticipantKit
      ? pForm.selectedKitIds.length
      : Number(pForm.quantity);
    const countLabel = isParticipantKit
      ? `${qty} kit${qty === 1 ? "" : "s"}`
      : `quantity ${qty}`;

    showAlert({
      title: "Confirm participant return",
      message: `Are you sure you want to post a participant return for ${countLabel}? You must sign and authenticate to complete this action.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign participant return",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this participant return. Return notes are required.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Return notes",
          descriptionPlaceholder: "Enter notes for this participant return…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        if (!notes) {
          setFormError("Return notes are required.");
          return;
        }
        await postParticipantReturnAfterAuth(notes, qty);
      },
    });
  };

  const postParticipantReturnAfterAuth = async (returnNotes, qty) => {
    setBusy(true);
    setFormError("");
    try {
      const notes = String(returnNotes || "").trim();
      const txnAt = toApiDateTimeString(pForm.txnDate, pForm.txnTime);

      if (isParticipantKit && selectedParticipantKits.length) {
        const byDispense = new Map();
        selectedParticipantKits.forEach((k) => {
          const dId = String(k.DispenseTxnId || "");
          if (!dId) return;
          if (!byDispense.has(dId)) {
            byDispense.set(dId, {
              dispenseTxnId: dId,
              impBatchId: k.ImpBatchId || "",
              kitIds: [],
            });
          }
          byDispense
            .get(dId)
            .kitIds.push(String(k.ImpKitId || k.impKitId || ""));
        });

        const groups = Array.from(byDispense.values());
        if (!groups.length) {
          setFormError("Select dispensed kits to return.");
          return;
        }

        for (let i = 0; i < groups.length; i += 1) {
          const g = groups[i];
          const result = await postImpReturn({
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            ImpBatchId:
              g.impBatchId ||
              selectedDispense?.ImpBatchId ||
              selectedDispense?.impBatchId,
            SourceTxnId: g.dispenseTxnId,
            Quantity: g.kitIds.length,
            TxnAt: txnAt,
            ConditionCode: pForm.conditionCode,
            StorageUnitId: pForm.storageUnitId,
            Notes: notes,
            ClientRequestId:
              i === 0 ? clientRequestId : newClientRequestId(),
            KitIdsJson: JSON.stringify(g.kitIds),
          });
          if (!result.success) {
            setFormError(
              result.message ||
                `Participant return failed for ${g.kitIds.length} kit(s).`,
            );
            return;
          }
        }
      } else {
        const result = await postImpReturn({
          StudyId: ctx.studyId,
          SiteId: ctx.siteId,
          ImpBatchId:
            selectedDispense?.ImpBatchId || selectedDispense?.impBatchId,
          SourceTxnId: pForm.dispenseTxnId,
          Quantity: qty,
          TxnAt: txnAt,
          ConditionCode: pForm.conditionCode,
          StorageUnitId: pForm.storageUnitId,
          Notes: notes,
          ClientRequestId: clientRequestId,
          KitIdsJson: null,
        });
        if (!result.success) {
          setFormError(result.message || "Participant return failed.");
          return;
        }
      }

      setModalMode(null);
      await refresh();
      if (overlay) onOverlaySuccess?.();
    } finally {
      setBusy(false);
    }
  };

  const onCreateParticipantReturnRequest = async () => {
    setFormError("");
    if (!canReturn && !canView) {
      setFormError("You do not have permission to record returns.");
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !pForm.dispenseTxnId) {
      setFormError(
        pForm.participantId
          ? "Select returnable kits (or a batch line) on the right."
          : "Select a participant.",
      );
      return;
    }
    if (!pForm.conditionCode) {
      setFormError("Return condition is required.");
      return;
    }
    if (!pForm.storageUnitId) {
      setFormError("Storage location is required.");
      return;
    }
    if (isParticipantKit) {
      if (!pForm.selectedKitIds.length) {
        setFormError("Select dispensed kits to return.");
        return;
      }
    } else if (!(Number(pForm.quantity) > 0)) {
      setFormError("Enter a positive return quantity.");
      return;
    }

    setRequestBusy(true);
    try {
      const qty = isParticipantKit
        ? pForm.selectedKitIds.length
        : Number(pForm.quantity);
      const result = await createReturnRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId:
          selectedDispense?.ImpBatchId || selectedDispense?.impBatchId,
        SourceTxnId: pForm.dispenseTxnId,
        Quantity: qty,
        TxnAt: toApiDateTimeString(pForm.txnDate, pForm.txnTime),
        ConditionCode: pForm.conditionCode,
        StorageUnitId: pForm.storageUnitId,
        Notes: "",
        ClientRequestId: clientRequestId,
        KitIdsJson: isParticipantKit
          ? JSON.stringify(pForm.selectedKitIds)
          : null,
      });
      if (!result.success) {
        setFormError(result.message || "Could not create return request.");
        return;
      }
      setModalMode(null);
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      await refresh();
      showAlert({
        title: "Return request created",
        message:
          "The request is on the Returns list. Completion-role staff were emailed when a form is required. Use Fill form on Due forms, then Confirm return after approval.",
        type: "success",
        buttonText: "OK",
      });
      if (overlay) onOverlaySuccess?.();
    } finally {
      setRequestBusy(false);
    }
  };

  const onSubmitStock = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!canReturn && !canView) {
      setFormError("You do not have permission to record returns.");
      return;
    }
    if (formBlocked || formGateBusy) {
      setFormError(
        formBlockMessage ||
          "Post stock return is blocked until the required form is completed and approved.",
      );
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !sForm.impBatchId) {
      setFormError(
        "Select available stock kits (or a batch line) on the right.",
      );
      return;
    }
    if (!sForm.destination) {
      setFormError("Return To (destination) is required.");
      return;
    }
    if (!sForm.reason) {
      setFormError("Stock return reason is required.");
      return;
    }
    if (isStockKit) {
      if (!sForm.selectedKitIds.length) {
        setFormError("Select kits for stock return.");
        return;
      }
    } else if (!(Number(sForm.quantity) > 0)) {
      setFormError("Enter a positive quantity.");
      return;
    }

    const qty = isStockKit
      ? sForm.selectedKitIds.length
      : Number(sForm.quantity);
    const countLabel = isStockKit
      ? `${qty} kit${qty === 1 ? "" : "s"}`
      : `quantity ${qty}`;

    showAlert({
      title: "Confirm stock return",
      message: `Are you sure you want to post a stock return for ${countLabel}? You must sign and authenticate to complete this action.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign stock return",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this stock return. Return notes are required.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Return notes",
          descriptionPlaceholder: "Enter notes for this stock return…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        if (!notes) {
          setFormError("Return notes are required.");
          return;
        }
        await postStockReturnAfterAuth(notes, qty);
      },
    });
  };

  const onCreateStockReturnRequest = async () => {
    setFormError("");
    if (!canReturn && !canView) {
      setFormError("You do not have permission to record returns.");
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !sForm.impBatchId) {
      setFormError(
        "Select available stock kits (or a batch line) on the right.",
      );
      return;
    }
    if (!sForm.destination) {
      setFormError("Return To (destination) is required.");
      return;
    }
    if (!sForm.reason) {
      setFormError("Stock return reason is required.");
      return;
    }
    if (isStockKit) {
      if (!sForm.selectedKitIds.length) {
        setFormError("Select kits for stock return.");
        return;
      }
    } else if (!(Number(sForm.quantity) > 0)) {
      setFormError("Enter a positive quantity.");
      return;
    }

    setRequestBusy(true);
    try {
      const qty = isStockKit
        ? sForm.selectedKitIds.length
        : Number(sForm.quantity);
      const ref = [
        sForm.destination,
        sForm.referenceNo.trim(),
        sForm.courierRef.trim(),
      ]
        .filter(Boolean)
        .join(" / ");
      const result = await createStockReturnRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: sForm.impBatchId,
        Quantity: qty,
        TxnAt: toApiDateTimeString(sForm.txnDate, sForm.txnTime),
        ConditionCode: sForm.reason,
        ReferenceNo: ref,
        Notes: "",
        SourceTxnId: sForm.sourceTxnId || null,
        ClientRequestId: clientRequestId,
        KitIdsJson: isStockKit ? JSON.stringify(sForm.selectedKitIds) : null,
      });
      if (!result.success) {
        setFormError(result.message || "Could not create stock return request.");
        return;
      }
      setModalMode(null);
      setStockFromPending(false);
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      await refresh();
      await refreshHeldSources();
      showAlert({
        title: "Return request created",
        message:
          "The request is on the Returns list. Completion-role staff were emailed when a form is required. Use Fill form on Due forms, then Confirm return after approval.",
        type: "success",
        buttonText: "OK",
      });
      if (overlay) onOverlaySuccess?.();
    } finally {
      setRequestBusy(false);
    }
  };

  const postStockReturnAfterAuth = async (returnNotes, qty) => {
    setBusy(true);
    setFormError("");
    try {
      const ref = [
        sForm.destination,
        sForm.referenceNo.trim(),
        sForm.courierRef.trim(),
      ]
        .filter(Boolean)
        .join(" / ");
      const result = await postImpStockReturn({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: sForm.impBatchId,
        Quantity: qty,
        TxnAt: toApiDateTimeString(sForm.txnDate, sForm.txnTime),
        ConditionCode: sForm.reason,
        ReferenceNo: ref,
        Notes: String(returnNotes || "").trim(),
        SourceTxnId: sForm.sourceTxnId || null,
        ClientRequestId: clientRequestId,
        KitIdsJson: isStockKit ? JSON.stringify(sForm.selectedKitIds) : null,
      });
      if (!result.success) {
        setFormError(result.message || "Stock return failed.");
        return;
      }
      setModalMode(null);
      setStockFromPending(false);
      await refresh();
      await refreshHeldSources();
      if (overlay) onOverlaySuccess?.();
    } finally {
      setBusy(false);
    }
  };

  const remaining = Number(
    selectedDispense?.RemainingReturnableQuantity ??
      selectedDispense?.remainingReturnableQuantity ??
      0,
  );

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      {!overlay ? (
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Returns" },
          ]}
          actions={[
            {
              icon: "fas fa-refresh",
              label: "Refresh",
              onClick: async () => {
                await refresh();
                await refreshHeldSources();
              },
            },
            {
              icon: "fas fa-user",
              label: "Record participant return",
              onClick: () => openAdd("participant"),
            },
            {
              icon: "fas fa-boxes",
              label: "Record stock return",
              onClick: () => openAdd("stock"),
            },
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        {ctx.studyId && ctx.siteId ? (
          <div className="epsf-return-summary" aria-label="Returns summary">
            <div className="epsf-return-summary__item epsf-return-summary__item--pending">
              <span className="epsf-return-summary__label">
                Awaiting stock return
              </span>
              <span className="epsf-return-summary__value">
                {returnSummary.pending}
              </span>
            </div>
            <div className="epsf-return-summary__item epsf-return-summary__item--participant">
              <span className="epsf-return-summary__label">
                Participant returns
              </span>
              <span className="epsf-return-summary__value">
                {returnSummary.participant}
              </span>
            </div>
            <div className="epsf-return-summary__item epsf-return-summary__item--stock">
              <span className="epsf-return-summary__label">
                Stock returns posted
              </span>
              <span className="epsf-return-summary__value">
                {returnSummary.stock}
              </span>
            </div>
            <div
              className={`epsf-return-summary__item epsf-return-summary__item--trend ${trendTone(
                weeklyTrend.completedPct,
              )}`}
              title={`Completed vs last week · this week ${weeklyTrend.thisWeekCompleted} · cancelled ${weeklyTrend.thisWeekCancelled} (${formatPctChange(weeklyTrend.cancelledPct)})`}
            >
              <span className="epsf-return-summary__label">
                Returns vs last week
              </span>
              <i
                className={`fas ${trendIconClass(weeklyTrend.completedPct)} epsf-return-summary__trend-icon`}
                aria-hidden="true"
              />
              <span className="epsf-return-summary__value epsf-return-summary__value--trend">
                {formatPctChange(weeklyTrend.completedPct)}
              </span>
            </div>
          </div>
        ) : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={unifiedTableRows}
            loading={
              participantLedger.loading || stockLedger.loading || heldLoading
            }
            fieldOptions={UNIFIED_FIELD_OPTIONS}
            defaultColumns={UNIFIED_DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              UNIFIED_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderCell}
            getRowKey={getUnifiedRowKey}
            persistLayoutKey="epsf-imp-returns-unified-v5"
            emptyMessage={
              !ctx.studyId || !ctx.siteId
                ? "Select study and site in the breadcrumb bar."
                : "No returns yet for this study and site."
            }
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
          />
        </div>
      </div>
      ) : null}

        {showModal && modalMode === "participant" ? (
          <div
            className="modal fade show d-block epsf-modal epsf-return-modal"
            tabIndex="-1"
            aria-modal="true"
          >
            <div className="modal-dialog modal-xl epsf-return-modal__dialog">
              <div className="modal-content epsf-return-modal__content">
                <form
                  onSubmit={onSubmitParticipant}
                  className="epsf-return-modal__form"
                >
                  <div className="modal-header py-2">
                    <div>
                      <h5 className="modal-title mb-0">
                        Record participant return
                      </h5>
                      <p className="epsf-return-modal__header-hint mb-0">
                        Participant → Pharmacy. Stock returns to review and is
                        not available for dispense until released.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={busy}
                    />
                  </div>
                  <div className="modal-body epsf-return-modal__body epsf-return-modal__body--split">
                    {formBlockMessage ? (
                      <div
                        className="alert alert-warning py-2 px-3 small mb-3"
                        role="status"
                      >
                        {formBlockMessage}
                      </div>
                    ) : null}
                    <div className="row g-3 epsf-return-participant-layout">
                      <div className="col-12 col-lg-7 epsf-return-participant-layout__form">
                        <div className="row g-3">
                          <div className="col-12">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Participant <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={participantOptions}
                                selected={findOption(
                                  participantOptions,
                                  pForm.participantId,
                                )}
                                onSelect={(opt) =>
                                  setPForm((prev) => ({
                                    ...prev,
                                    participantId: opt?.value || "",
                                    dispenseTxnId: "",
                                    selectedKitIds: [],
                                    quantity: "",
                                  }))
                                }
                                showSearch
                              />
                            </div>
                          </div>

                          {pForm.participantId ? (
                            <div className="col-12">
                              <div className="epsf-return-summary-card">
                                <div className="epsf-return-summary-card__row">
                                  <div>
                                    <span className="epsf-return-summary-card__label">
                                      Participant
                                    </span>
                                    <div className="epsf-return-summary-card__value">
                                      {selectedParticipantLabel || "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="epsf-return-summary-card__label">
                                      IMP / Batch
                                    </span>
                                    <div className="epsf-return-summary-card__value">
                                      {selectedKitBatchSummary
                                        ? `${selectedKitBatchSummary.productCode || "—"} / ${
                                            selectedKitBatchSummary.batchNumber ||
                                            "—"
                                          }`
                                        : selectedDispense
                                          ? `${selectedDispense.ProductCode || selectedDispense.productCode || "—"} / ${
                                              selectedDispense.BatchNumber ||
                                              selectedDispense.batchNumber ||
                                              "—"
                                            }`
                                          : "Pick kits on the right"}
                                    </div>
                                  </div>
                                </div>
                                {selectedKitBatchSummary ? (
                                  <div className="epsf-return-summary-card__stats">
                                    <div>
                                      <span>Dispensed</span>
                                      <strong>
                                        {selectedKitBatchSummary.dispensed}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Already returned</span>
                                      <strong>
                                        {selectedKitBatchSummary.already}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Remaining</span>
                                      <strong>
                                        {selectedKitBatchSummary.remaining}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Selected kits</span>
                                      <strong>
                                        {selectedKitBatchSummary.selectedCount}
                                      </strong>
                                    </div>
                                  </div>
                                ) : selectedDispense ? (
                                  <div className="epsf-return-summary-card__stats">
                                    <div>
                                      <span>Dispensed</span>
                                      <strong>
                                        {selectedDispense.DispensedQuantity ??
                                          selectedDispense.dispensedQuantity ??
                                          "—"}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Already returned</span>
                                      <strong>
                                        {selectedDispense.AlreadyReturnedQuantity ??
                                          selectedDispense.alreadyReturnedQuantity ??
                                          0}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Remaining</span>
                                      <strong>{remaining}</strong>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="epsf-return-summary-card__hint mb-0">
                                    Select returnable kits on the right to fill
                                    IMP, batch and quantities.
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : null}

                          {!isParticipantKit && selectedDispense ? (
                            <div className="col-12">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Return quantity{" "}
                                  <span className="mandatory">*</span>
                                </label>
                                <input
                                  className="form-control"
                                  type="number"
                                  min="0.0001"
                                  step="any"
                                  value={pForm.quantity}
                                  onChange={(ev) =>
                                    setPForm((prev) => ({
                                      ...prev,
                                      quantity: ev.target.value,
                                    }))
                                  }
                                  required
                                />
                              </div>
                            </div>
                          ) : null}

                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Condition <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={CONDITION_OPTIONS}
                                selected={findOption(
                                  CONDITION_OPTIONS,
                                  pForm.conditionCode,
                                )}
                                onSelect={(opt) =>
                                  setPForm((prev) => ({
                                    ...prev,
                                    conditionCode: opt?.value || "",
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Storage location{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={unitOptions}
                                selected={findOption(
                                  unitOptions,
                                  pForm.storageUnitId,
                                )}
                                onSelect={(opt) =>
                                  setPForm((prev) => ({
                                    ...prev,
                                    storageUnitId: opt?.value || "",
                                  }))
                                }
                                showSearch
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Return date{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <SegmentDatePicker
                                selected={pForm.txnDate}
                                onChange={(d) =>
                                  setPForm((prev) => ({ ...prev, txnDate: d }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Return time{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <SegmentTimePicker
                                value={pForm.txnTime}
                                onChange={(t) =>
                                  setPForm((prev) => ({ ...prev, txnTime: t }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                        {formError ? (
                          <p className="epsf-list-page__error mt-3 mb-0">
                            {formError}
                          </p>
                        ) : null}
                      </div>

                      <div className="col-12 col-lg-5 epsf-return-participant-layout__kits">
                        <aside
                          className="epsf-return-kits-panel"
                          aria-label="Returnable kits"
                        >
                          <div className="epsf-return-kits-panel__head">
                            <h6 className="epsf-return-kits-panel__title">
                              Returnable kits
                            </h6>
                            {pForm.participantId &&
                            eligibleParticipantKits.length ? (
                              <span className="epsf-return-kits-panel__count">
                                {pForm.selectedKitIds.length} /{" "}
                                {eligibleParticipantKits.length} selected
                              </span>
                            ) : null}
                          </div>

                          {!pForm.participantId ? (
                            <p className="epsf-return-kits-panel__empty">
                              Select a participant on the left to see kits
                              available to return.
                            </p>
                          ) : eligibleParticipantKits.length ||
                            eligibleBatchDispenses.length ? (
                            <>
                              {eligibleParticipantKits.length ? (
                                <div
                                  className="epsf-return-kits-panel__list epsf-return-kits-tree"
                                  role="tree"
                                  aria-label="Batches and returnable kits"
                                >
                                  {participantKitGroups.map((group) => {
                                    const treeKey = String(
                                      group.groupKey ||
                                        `${group.productCode}|${group.batchNumber}` ||
                                        "kits",
                                    );
                                    const open =
                                      !collapsedKitTreeBatches.has(treeKey);
                                    const batchTitle =
                                      [
                                        group.productCode,
                                        group.batchNumber,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ") || "Kits";
                                    const kitIds = (group.kits || [])
                                      .map((k) =>
                                        String(k.ImpKitId || k.impKitId || ""),
                                      )
                                      .filter(Boolean);
                                    const selectedInBatch = kitIds.filter(
                                      (id) =>
                                        pForm.selectedKitIds.includes(id),
                                    );
                                    const allSelected =
                                      kitIds.length > 0 &&
                                      selectedInBatch.length === kitIds.length;
                                    const someSelected =
                                      selectedInBatch.length > 0 &&
                                      !allSelected;
                                    return (
                                      <div
                                        key={treeKey}
                                        className={`epsf-return-kits-tree__node${
                                          open
                                            ? " epsf-return-kits-tree__node--open"
                                            : ""
                                        }`}
                                        role="treeitem"
                                        aria-expanded={open}
                                      >
                                        <div className="epsf-return-kits-tree__batch">
                                          <button
                                            type="button"
                                            className="epsf-return-kits-tree__toggle"
                                            onClick={() =>
                                              toggleKitTreeBatch(treeKey)
                                            }
                                            aria-label={`${
                                              open ? "Collapse" : "Expand"
                                            } ${batchTitle}`}
                                          >
                                            <i
                                              className={`fas fa-chevron-${
                                                open ? "down" : "right"
                                              }`}
                                              aria-hidden="true"
                                            />
                                          </button>
                                          <input
                                            type="checkbox"
                                            className="epsf-return-kits-tree__batch-check"
                                            checked={allSelected}
                                            ref={(el) => {
                                              if (el)
                                                el.indeterminate = someSelected;
                                            }}
                                            onChange={() =>
                                              toggleParticipantBatchAllKits(
                                                group,
                                              )
                                            }
                                            aria-label={`Select all kits in ${batchTitle}`}
                                            title="Select all kits in this batch"
                                          />
                                          <button
                                            type="button"
                                            className="epsf-return-kits-tree__batch-main"
                                            onClick={() =>
                                              toggleKitTreeBatch(treeKey)
                                            }
                                          >
                                            <span className="epsf-return-kits-tree__batch-name">
                                              {batchTitle}
                                            </span>
                                            <span className="epsf-return-kits-tree__batch-count">
                                              {group.kits.length} kit
                                              {group.kits.length === 1
                                                ? ""
                                                : "s"}
                                            </span>
                                          </button>
                                        </div>
                                        {open ? (
                                          <ul
                                            className="epsf-return-kits-tree__children"
                                            role="group"
                                          >
                                            {group.kits.map((k) => {
                                              const id = String(
                                                k.ImpKitId || k.impKitId || "",
                                              );
                                              const checked =
                                                pForm.selectedKitIds.includes(
                                                  id,
                                                );
                                              return (
                                                <li key={id} role="treeitem">
                                                  <label
                                                    className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat${
                                                      checked
                                                        ? " epsf-return-kits-panel__item--selected"
                                                        : ""
                                                    }`}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={checked}
                                                      onChange={() =>
                                                        toggleKit(id, "p")
                                                      }
                                                    />
                                                    <span className="epsf-return-kits-panel__kit-no">
                                                      {k.KitNumber ||
                                                        k.kitNumber ||
                                                        id}
                                                    </span>
                                                    {renderReceiptStatusBadge(
                                                      k.Status ||
                                                        k.status ||
                                                        "Dispensed",
                                                    )}
                                                  </label>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}

                              {eligibleBatchDispenses.length ? (
                                <div className="epsf-return-kits-panel__batch">
                                  <div className="epsf-return-kits-panel__group-label">
                                    Batch quantity lines
                                  </div>
                                  <ul className="epsf-return-kits-panel__list-inner">
                                    {eligibleBatchDispenses.map((d) => {
                                      const id = String(
                                        d.DispenseTxnId || d.dispenseTxnId || "",
                                      );
                                      const selected =
                                        pForm.dispenseTxnId === id &&
                                        !pForm.selectedKitIds.length;
                                      const rem =
                                        d.RemainingReturnableQuantity ??
                                        d.remainingReturnableQuantity ??
                                        0;
                                      return (
                                        <li key={id}>
                                          <button
                                            type="button"
                                            className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--btn${
                                              selected
                                                ? " epsf-return-kits-panel__item--selected"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              selectBatchDispenseLine(id)
                                            }
                                          >
                                            <span className="epsf-return-kits-panel__kit-no">
                                              {[
                                                d.ProductCode || d.productCode,
                                                d.BatchNumber || d.batchNumber,
                                              ]
                                                .filter(Boolean)
                                                .join(" · ") || "Batch line"}
                                            </span>
                                            <span className="epsf-return-kits-panel__kit-status">
                                              Remaining {rem}
                                            </span>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <p className="epsf-return-kits-panel__empty">
                              No returnable kits or batch lines for this
                              participant.
                            </p>
                          )}
                        </aside>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer py-2 gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy || requestBusy}
                    >
                      Cancel
                    </button>
                    {SHOW_RETURN_CREATE_REQUEST ? (
                      <button
                        type="button"
                        className={
                          needsRequest || formBlocked
                            ? "btn btn-primary"
                            : "btn btn-outline-primary"
                        }
                        onClick={onCreateParticipantReturnRequest}
                        disabled={busy || requestBusy || formGateBusy}
                      >
                        {requestBusy ? "Creating..." : "Create request"}
                      </button>
                    ) : null}
                    <button
                      type="submit"
                      className={
                        SHOW_RETURN_CREATE_REQUEST &&
                        (needsRequest || formBlocked)
                          ? "btn btn-outline-primary"
                          : "btn btn-primary"
                      }
                      disabled={
                        busy || requestBusy || formGateBusy || formBlocked
                      }
                    >
                      {busy ? "Saving..." : "Post participant return"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}

        {showModal && modalMode === "stock" ? (
          <div
            className="modal fade show d-block epsf-modal epsf-return-modal"
            tabIndex="-1"
            aria-modal="true"
          >
            <div className="modal-dialog modal-xl epsf-return-modal__dialog">
              <div className="modal-content epsf-return-modal__content">
                <form
                  onSubmit={onSubmitStock}
                  className="epsf-return-modal__form"
                >
                  <div className="modal-header py-2">
                    <h5 className="modal-title mb-0">Record stock return</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={busy}
                    />
                  </div>
                  <div className="modal-body epsf-return-modal__body epsf-return-modal__body--split">
                    {formBlockMessage ? (
                      <div
                        className="alert alert-warning py-2 px-3 small mb-3"
                        role="status"
                      >
                        {formBlockMessage}
                      </div>
                    ) : null}
                    <div className="row g-3 epsf-return-stock-layout">
                      <div className="col-12 col-lg-7 epsf-return-stock-layout__form">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Return To <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={DESTINATION_OPTIONS}
                                selected={findOption(
                                  DESTINATION_OPTIONS,
                                  sForm.destination,
                                )}
                                onSelect={(opt) =>
                                  setSForm((prev) => ({
                                    ...prev,
                                    destination: opt?.value || "",
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Reason <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={STOCK_REASON_OPTIONS}
                                selected={findOption(
                                  STOCK_REASON_OPTIONS,
                                  sForm.reason,
                                )}
                                onSelect={(opt) =>
                                  setSForm((prev) => ({
                                    ...prev,
                                    reason: opt?.value || "",
                                  }))
                                }
                              />
                            </div>
                          </div>

                          {stockFromPending ? (
                            <div className="col-12">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Return source{" "}
                                  <span className="mandatory">*</span>
                                </label>
                                <input
                                  className="form-control"
                                  readOnly
                                  value={pendingSourceSummary}
                                  title={pendingSourceSummary}
                                />
                              </div>
                            </div>
                          ) : null}

                          {sForm.impBatchId ||
                          selectedStockKitBatchSummary ? (
                            <div className="col-12">
                              <div className="epsf-return-summary-card">
                                <div className="epsf-return-summary-card__row">
                                  <div>
                                    <span className="epsf-return-summary-card__label">
                                      IMP / Batch
                                    </span>
                                    <div className="epsf-return-summary-card__value">
                                      {selectedStockKitBatchSummary
                                        ? `${selectedStockKitBatchSummary.productCode || "—"} / ${
                                            selectedStockKitBatchSummary.batchNumber ||
                                            "—"
                                          }`
                                        : "Pick stock on the right"}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="epsf-return-summary-card__label">
                                      Source
                                    </span>
                                    <div className="epsf-return-summary-card__value">
                                      {sForm.sourceTxnId
                                        ? "Held receipt"
                                        : "Available / on-hand"}
                                    </div>
                                  </div>
                                </div>
                                {selectedStockKitBatchSummary ? (
                                  <div className="epsf-return-summary-card__stats">
                                    <div>
                                      <span>On hand</span>
                                      <strong>
                                        {selectedStockKitBatchSummary.onHand}
                                      </strong>
                                    </div>
                                    <div>
                                      <span>Held</span>
                                      <strong>
                                        {selectedStockKitBatchSummary.held}
                                      </strong>
                                    </div>
                                    {isStockKit ? (
                                      <div>
                                        <span>Selected kits</span>
                                        <strong>
                                          {
                                            selectedStockKitBatchSummary.selectedCount
                                          }
                                        </strong>
                                      </div>
                                    ) : (
                                      <div>
                                        <span>Return qty</span>
                                        <strong>
                                          {sForm.quantity || "—"}
                                        </strong>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="epsf-return-summary-card__hint mb-0">
                                    Select kits or a batch line on the right to
                                    fill IMP, batch and quantities.
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="epsf-return-summary-card">
                                <p className="epsf-return-summary-card__hint mb-0">
                                  Select available stock kits on the right to
                                  fill return details.
                                </p>
                              </div>
                            </div>
                          )}

                          {!stockFromPending && sForm.impBatchId ? (
                            <div className="col-12">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Held receipt (optional)
                                </label>
                                <Dropdown
                                  options={stockSourceOptions}
                                  selected={findOption(
                                    stockSourceOptions,
                                    sForm.sourceTxnId,
                                  )}
                                  onSelect={(opt) =>
                                    setSForm((prev) => ({
                                      ...prev,
                                      sourceTxnId: opt?.value || "",
                                      selectedKitIds: [],
                                      quantity: "",
                                    }))
                                  }
                                  showSearch
                                />
                                <p className="form-text mb-0 mt-1">
                                  Leave as available / on-hand, or pick a held
                                  receipt on this batch.
                                </p>
                              </div>
                            </div>
                          ) : null}

                          {!isStockKit && sForm.impBatchId ? (
                            <div className="col-12">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Quantity{" "}
                                  <span className="mandatory">*</span>
                                </label>
                                <input
                                  className="form-control"
                                  type="number"
                                  min="0.0001"
                                  step="any"
                                  value={sForm.quantity}
                                  onChange={(ev) =>
                                    setSForm((prev) => ({
                                      ...prev,
                                      quantity: ev.target.value,
                                    }))
                                  }
                                  required
                                />
                              </div>
                            </div>
                          ) : null}

                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                RMA / Reference
                              </label>
                              <input
                                className="form-control"
                                value={sForm.referenceNo}
                                onChange={(ev) =>
                                  setSForm((prev) => ({
                                    ...prev,
                                    referenceNo: ev.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Courier / Shipment
                              </label>
                              <input
                                className="form-control"
                                value={sForm.courierRef}
                                onChange={(ev) =>
                                  setSForm((prev) => ({
                                    ...prev,
                                    courierRef: ev.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Return date{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <SegmentDatePicker
                                selected={sForm.txnDate}
                                onChange={(d) =>
                                  setSForm((prev) => ({ ...prev, txnDate: d }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Return time{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <SegmentTimePicker
                                value={sForm.txnTime}
                                onChange={(t) =>
                                  setSForm((prev) => ({ ...prev, txnTime: t }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                        {formError ? (
                          <p className="epsf-list-page__error mt-3 mb-0">
                            {formError}
                          </p>
                        ) : null}
                      </div>

                      <div className="col-12 col-lg-5 epsf-return-stock-layout__track">
                        <aside
                          className="epsf-return-kits-panel"
                          aria-label="Available stock kits"
                        >
                          <div className="epsf-return-kits-panel__head">
                            <h6 className="epsf-return-kits-panel__title">
                              Available stock
                            </h6>
                            {isStockKit && sForm.impBatchId ? (
                              <span className="epsf-return-kits-panel__count">
                                {sForm.selectedKitIds.length} /{" "}
                                {eligibleStockKits.length} selected
                              </span>
                            ) : null}
                          </div>

                          <div className="epsf-return-kits-panel__search">
                            <i
                              className="fas fa-search epsf-return-kits-panel__search-icon"
                              aria-hidden="true"
                            />
                            <input
                              type="search"
                              className="epsf-return-kits-panel__search-input"
                              placeholder="Search batch or kit…"
                              value={stockKitSearch}
                              onChange={(e) =>
                                setStockKitSearch(e.target.value)
                              }
                              aria-label="Search batch or kit"
                            />
                          </div>

                          {filteredStockReturnKitGroups.length ? (
                            <div
                              className="epsf-return-kits-panel__list epsf-return-kits-tree"
                              role="tree"
                              aria-label="Batches and kits"
                            >
                              {filteredStockReturnKitGroups.map((group) => {
                                const treeKey = String(group.batchId || "");
                                const open =
                                  !collapsedKitTreeBatches.has(treeKey);
                                const batchTitle =
                                  [group.productCode, group.batchNumber]
                                    .filter(Boolean)
                                    .join(" · ") || "Batch";
                                const kitIds =
                                  group.type === "kits"
                                    ? (group.kits || [])
                                        .map((k) =>
                                          String(
                                            k.ImpKitId || k.impKitId || "",
                                          ),
                                        )
                                        .filter(Boolean)
                                    : [];
                                const selectedInBatch = kitIds.filter((id) =>
                                  sForm.selectedKitIds.includes(id),
                                );
                                const allSelected =
                                  kitIds.length > 0 &&
                                  selectedInBatch.length === kitIds.length;
                                const someSelected =
                                  selectedInBatch.length > 0 && !allSelected;
                                const countLabel =
                                  group.type === "kits"
                                    ? `${group.kits.length} kit${
                                        group.kits.length === 1 ? "" : "s"
                                      }`
                                    : `On hand ${group.onHand}`;
                                return (
                                  <div
                                    key={treeKey}
                                    className={`epsf-return-kits-tree__node${
                                      open
                                        ? " epsf-return-kits-tree__node--open"
                                        : ""
                                    }`}
                                    role="treeitem"
                                    aria-expanded={open}
                                  >
                                    <div className="epsf-return-kits-tree__batch">
                                      <button
                                        type="button"
                                        className="epsf-return-kits-tree__toggle"
                                        onClick={() =>
                                          toggleKitTreeBatch(treeKey)
                                        }
                                        aria-label={`${
                                          open ? "Collapse" : "Expand"
                                        } ${batchTitle}`}
                                      >
                                        <i
                                          className={`fas fa-chevron-${
                                            open ? "down" : "right"
                                          }`}
                                          aria-hidden="true"
                                        />
                                      </button>
                                      {group.type === "kits" ? (
                                        <input
                                          type="checkbox"
                                          className="epsf-return-kits-tree__batch-check"
                                          checked={allSelected}
                                          ref={(el) => {
                                            if (el)
                                              el.indeterminate = someSelected;
                                          }}
                                          onChange={() =>
                                            toggleStockBatchAllKits(group)
                                          }
                                          aria-label={`Select all kits in ${batchTitle}`}
                                          title="Select all kits in this batch"
                                        />
                                      ) : (
                                        <input
                                          type="checkbox"
                                          className="epsf-return-kits-tree__batch-check"
                                          checked={
                                            sForm.impBatchId ===
                                              group.batchId &&
                                            !sForm.selectedKitIds.length
                                          }
                                          onChange={() => {
                                            if (
                                              sForm.impBatchId ===
                                                group.batchId &&
                                              !sForm.selectedKitIds.length
                                            ) {
                                              setSForm((prev) => ({
                                                ...prev,
                                                impBatchId: stockFromPending
                                                  ? prev.impBatchId
                                                  : "",
                                                quantity: "",
                                              }));
                                            } else {
                                              selectStockBatchLine(
                                                group.batchId,
                                              );
                                            }
                                          }}
                                          aria-label={`Select batch ${batchTitle}`}
                                        />
                                      )}
                                      <button
                                        type="button"
                                        className="epsf-return-kits-tree__batch-main"
                                        onClick={() =>
                                          toggleKitTreeBatch(treeKey)
                                        }
                                      >
                                        <span className="epsf-return-kits-tree__batch-name">
                                          {batchTitle}
                                        </span>
                                        <span className="epsf-return-kits-tree__batch-count">
                                          {countLabel}
                                        </span>
                                      </button>
                                    </div>
                                    {open ? (
                                      group.type === "kits" ? (
                                        <ul
                                          className="epsf-return-kits-tree__children"
                                          role="group"
                                        >
                                          {group.kits.map((k) => {
                                            const id = String(
                                              k.ImpKitId || k.impKitId || "",
                                            );
                                            const checked =
                                              sForm.selectedKitIds.includes(id);
                                            const status =
                                              k.Status || k.status || "";
                                            return (
                                              <li key={id} role="treeitem">
                                                <label
                                                  className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat${
                                                    checked
                                                      ? " epsf-return-kits-panel__item--selected"
                                                      : ""
                                                  }`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() =>
                                                      toggleKit(id, "s")
                                                    }
                                                  />
                                                  <span className="epsf-return-kits-panel__kit-no">
                                                    {k.KitNumber ||
                                                      k.kitNumber ||
                                                      id}
                                                  </span>
                                                  {status
                                                    ? renderReceiptStatusBadge(
                                                        status,
                                                      )
                                                    : null}
                                                </label>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      ) : (
                                        <ul
                                          className="epsf-return-kits-tree__children"
                                          role="group"
                                        >
                                          <li role="treeitem">
                                            <button
                                              type="button"
                                              className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat epsf-return-kits-panel__item--btn${
                                                sForm.impBatchId ===
                                                  group.batchId &&
                                                !sForm.selectedKitIds.length
                                                  ? " epsf-return-kits-panel__item--selected"
                                                  : ""
                                              }`}
                                              onClick={() =>
                                                selectStockBatchLine(
                                                  group.batchId,
                                                )
                                              }
                                            >
                                              <span className="epsf-return-kits-panel__kit-no">
                                                Batch quantity return
                                              </span>
                                              <span className="epsf-return-kits-panel__meta">
                                                On hand {group.onHand}
                                              </span>
                                            </button>
                                          </li>
                                        </ul>
                                      )
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="epsf-return-kits-panel__empty">
                              {stockKitSearch.trim()
                                ? "No batches or kits match this search."
                                : "No available batches or kits to return for this study / site."}
                            </p>
                          )}
                        </aside>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer py-2 epsf-return-modal__footer">
                    <span
                      className="epsf-return-modal__footer-note"
                      role="status"
                    >
                      Pharmacy → Sponsor / Depot / Supplier. No participant.
                      Negative ledger movement.
                    </span>
                    <div className="epsf-return-modal__footer-actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={closeModal}
                        disabled={busy || requestBusy}
                      >
                        Cancel
                      </button>
                      {SHOW_RETURN_CREATE_REQUEST ? (
                        <button
                          type="button"
                          className={
                            needsRequest || formBlocked
                              ? "btn btn-primary"
                              : "btn btn-outline-primary"
                          }
                          onClick={onCreateStockReturnRequest}
                          disabled={busy || requestBusy || formGateBusy}
                        >
                          {requestBusy ? "Creating..." : "Create request"}
                        </button>
                      ) : null}
                      <button
                        type="submit"
                        className={
                          SHOW_RETURN_CREATE_REQUEST &&
                          (needsRequest || formBlocked)
                            ? "btn btn-outline-primary"
                            : "btn btn-primary"
                        }
                        disabled={
                          busy || requestBusy || formGateBusy || formBlocked
                        }
                      >
                        {busy ? "Saving..." : "Post stock return"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
